import { useState, useEffect } from "react";

function Home() {
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [role, setRole] = useState("Pilot");
  const [reasonForContact, setReasonForContact] = useState("");
  const [activeTab, setActiveTab] = useState("custom_database");
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0); // State for video carousel

  // Array of your video files
  const heroVideos = ["yc_video.mp4", "ec_video.mp4"];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Updated tabsData array with new use cases and text
  const tabsData = [
    {
      id: "custom_database",
      label: "Custom Database",
      mainHeading: "Build Your Custom Database",
      bulletPoints: [
        "Create comprehensive databases of people, companies, or any entity",
        "Extract structured data from multiple sources automatically",
        "Keep your database updated with scheduled refreshes",
        "Export to your preferred format - SQL, CSV, JSON, or API",
      ],
      platforms: ["GitHub Profiles", "Company Directories", "Personal Websites"],
    },
    {
      id: "custom_request",
      label: "Custom Data Request",
      status: "contact",
      mainHeading: "Your Custom Data Need",
      bulletPoints: [
        "Target niche portals, public datasets, or open APIs",
        "Seamlessly pipe data into Excel, Google Sheets, or your existing system",
        "Handle large and messy datasets? We want to hear it!",
        "Need something wilder? Complex flows, weird formats - we're all ears",
      ],
      platforms: ["Any publicly accessible website", "Industry-Specific Portals", "Open Data Initiatives"],
    },
    {
      id: "catalog_export",
      label: "SEO & E-Commerce Intelligence",
      mainHeading: "Export & Benchmark Product Catalogs",
      bulletPoints: [
        "Compare competitor keywords & pricing",
        "Optimize titles and SEO metadata",
        "Export entire product listings (titles, prices, descriptions, etc)",
        "Pull store products through 1 Google Sheet add on for simple data extraction",
      ],
      platforms: ["Company Websites", "Shopify Pages", "Publicly Accessible Sources"],
    },
    {
      id: "quantitative_finance",
      label: "Quantitative Finance",
      status: "coming_soon",
      mainHeading: "Power Your Trading Models with Data",
      bulletPoints: [
        "Stream news and sentiment data from financial news sites",
        "Feed real-time market data into your trading algorithms",
        "Track regulatory filings and corporate announcements",
        "Monitor social media sentiment for market signals",
      ],
      platforms: ["Financial News Sites", "Reuters", "SEC Filings", "Social Media"],
    },
  ];

  const activeTabContent = tabsData.find((tab) => tab.id === activeTab);

  // Function to get the correct content for the right side of the tab
  const getTabContentRightSide = () => {
    switch (activeTab) {
      case 'custom_database':
        return { type: 'image', src: '/images/keyword_extraction.png', alt: 'Custom Database Use Case' };
      case 'custom_request':
        return { type: 'link' }; // Show the calendly link
      case 'quantitative_finance':
        return { type: 'image', src: '/images/quant.png', alt: 'Quantitative Finance Use Case' };
      case 'catalog_export':
        return { type: 'image', src: '/images/window_shopping.png', alt: 'E-commerce Use Case' };
      default:
        return { type: 'image', src: '', alt: '' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Include reasonForContact in formData
    const formData = { email, role, reasonForContact };
    try {
      await fetch("https://submit-form.com/OOy1NWTF1", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      setSuccessMessage("Thanks! We'll be in touch.");
      setEmail("");
      setRole("Pilot");
      setReasonForContact(""); // Clear the textarea after submission
    } catch {
      setSuccessMessage("Thanks! We'll be in touch."); // Fallback success for now
      setEmail("");
      setRole("Pilot");
      setReasonForContact(""); // Clear the textarea even on error fallback
    }
  };

  const tabRightSideContent = getTabContentRightSide();

  // Functions to navigate the video carousel
  const goToNextVideo = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % heroVideos.length);
  };

  const goToPrevVideo = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex - 1 + heroVideos.length) % heroVideos.length);
  };

  // Function to handle video ending and advance to the next
  const handleVideoEnded = () => {
    goToNextVideo();
  };

  return (
    <section className="text-white min-h-screen flex flex-col items-center relative">
      {/* Animated background effect */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(9, 80, 112, 0.15) 0%, transparent 50%)`
        }}
      />
      
      {/* Hero Section with Video Carousel */}
      <div className="max-w-7xl w-full flex flex-col md:flex-row items-center justify-between space-y-10 md:space-y-0 py-12 mt-[11%] px-6 relative z-10">
        <div className="md:w-[60%] text-center md:text-left">
          <h1 className="text-5xl md:text-6xl font-black animate-fadeInUp leading-tight">
            Data You Need.
            <br />
            <span className="gradient-text">Delivered Anywhere.</span>
          </h1>
          <p className="text-gray-400 text-lg mt-6 animate-fadeInUp delay-200 max-w-lg">
            Extract web data at scale. Dodge bot detection. Clean results to Google Sheets, Excel, or your API.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center md:justify-start animate-fadeInUp delay-400">
            <a
              href="/dev"
              className="group relative px-8 py-4 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-lg font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>For Developers</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </span>
            </a>
            <button
              onClick={() => document.getElementById('use-cases').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 glass rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Use Cases
            </button>
          </div>
        </div>

        {/* Demo Video Carousel */}
        <div className="md:w-[40%] w-full animate-fadeInUp delay-200 relative">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl" />
            <div className="relative glass rounded-2xl shadow-xl overflow-hidden">
              <video
                key={currentVideoIndex} // Key prop to force re-render and restart video
                src={heroVideos[currentVideoIndex]}
                autoPlay
                muted
                playsInline
                controls
                onEnded={handleVideoEnded} // Added onEnded event handler
                className="w-full h-auto rounded-2xl"
              />
            </div>
          </div>
           {/* Carousel Indicators (positioned below the video) */}
           <div className="mt-4 flex justify-center space-x-2">
            {heroVideos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentVideoIndex(index)}
                className={`w-[14px] h-[14px] rounded-full transition-all duration-300 ${
                  currentVideoIndex === index ? 'bg-[#25BC82]' : 'bg-gray-500 hover:bg-gray-300'
                }`}
                aria-label={`Go to video ${index + 1}`}
              ></button>
            ))}
          </div>
          {/* New: Text below the video */}
          <p className="text-center text-gray-300 text-lg mt-3">
            Example Vidoes: Get Internet Data in Google Sheets.
          </p>
        </div>
      </div>

      {/* Let's Connect Form Section */}
      <section id="contact-form" className="w-full py-20 relative">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative">
            <div className="relative bg-gray-900/50 rounded-3xl p-12 backdrop-blur-xl border border-gray-800 hover:border-green-400 transition-all duration-300">
              <div className="text-center mb-10">
                <h2 className="text-5xl md:text-6xl font-black mb-4">
                  <span className="gradient-text">Ready to Start?</span>
                </h2>
                <p className="text-gray-400 text-xl max-w-2xl mx-auto">Tell us about your data needs and let's build something amazing together</p>
              </div>
              <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Project Type</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200"
                      required
                    >
                      <option value="Pilot">Pilot Project</option>
                      <option value="Developer">Developer</option>
                      <option value="Integration Partner">Partner</option>
                    </select>
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200"
                      required
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-400 mb-2">Message</label>
                  <textarea
                    placeholder="Tell us about your specific data extraction needs, what sources you're interested in, and how you'd like the data delivered..."
                    value={reasonForContact}
                    onChange={(e) => setReasonForContact(e.target.value)}
                    rows="5"
                    className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200 resize-none"
                  />
                </div>
                <div className="text-center">
                  <button
                    type="submit"
                    className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-[#095070] to-[#25BC82] text-white px-10 py-5 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#25BC82]/20"
                  >
                    <span>Send Message</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>
              </form>
              {successMessage && (
                <div className="mt-8 p-5 bg-green-500/10 backdrop-blur-sm rounded-xl border border-green-500/20 max-w-2xl mx-auto">
                  <p className="text-green-400 text-center text-lg font-medium">{successMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* New 3-Step Process Section */}
      <section className="max-w-7xl w-full px-6 py-20 relative">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="gradient-text">How It Works</span>
          </h2>
          <p className="text-gray-400 text-lg">Three simple steps to your data</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connection lines for desktop */}
          <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-gray-700 to-transparent -translate-y-1/2" />
          
          {/* Step 1 */}
          <div className="relative">
            <div className="relative bg-gray-900/50 rounded-3xl p-8 h-full backdrop-blur-xl border border-gray-800 hover:border-green-400 transition-all duration-300 hover:scale-[1.015]">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <span className="text-5xl">🌐</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Acquire</h3>
              <p className="text-gray-400">Resilient extraction at scale</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative">
            <div className="relative bg-gray-900/50 rounded-3xl p-8 h-full backdrop-blur-xl hover:border-green-400 border border-gray-800 transition-all duration-300 hover:scale-[1.015]">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 delay-200">
                <span className="text-5xl">⚙️</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Transform</h3>
              <p className="text-gray-400">Clean & structure your data</p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative">
            <div className="relative bg-gray-900/50 rounded-3xl p-8 h-full backdrop-blur-xl hover:border-green-400 border border-gray-800 transition-all duration-300 hover:scale-[1.015]">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 delay-400">
                <span className="text-5xl">📊</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Deliver</h3>
              <p className="text-gray-400">Google Sheets, Excel, your API - anywhere</p>
            </div>
          </div>
        </div>
      </section>


      {/* Tabbed Use Cases Section */}
      <section id="use-cases" className="max-w-7xl w-full px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="gradient-text">Use Cases</span>
          </h2>
          <p className="text-gray-400 text-lg">Tailored solutions for your industry</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {tabsData.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-[#095070] to-[#25BC82] text-white scale-105 shadow-lg"
                  : "glass text-gray-400 hover:text-white hover:border-green-400"
              }`}
            >
              {tab.label}
              {tab.status === "coming_soon" && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400">
                  Soon
                </span>
              )}
              {tab.status === "contact" && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400">
                  Contact
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content Area */}
        <div className="relative bg-gray-900/50 rounded-3xl p-10 min-h-[500px] backdrop-blur-xl border border-gray-800 hover:border-green-400 transition-all duration-300 animate-fadeIn">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h3 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                {activeTabContent.mainHeading}
              </h3>
              <ul className="space-y-4 mb-8">
                {activeTabContent.bulletPoints.slice(0, 3).map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-300">
                    <span className="text-[#25BC82] text-xl mt-1">✓</span>
                    <span className="text-lg">{point}</span>
                  </li>
                ))}
              </ul>
              {activeTabContent.platforms && (
                <div className="flex flex-wrap gap-3">
                  {activeTabContent.platforms.map((platform, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 glass rounded-full text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300 cursor-pointer"
                    >
                      {platform}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Right side content */}
            <div className="md:w-1/2 flex justify-center items-center">
              {tabRightSideContent.type === "image" ? (
                <div className="relative group">
                  <div className="relative w-full max-w-[300px] md:max-w-lg lg:max-w-xl h-[250px] md:h-[400px] lg:h-[450px] overflow-hidden rounded-2xl flex items-center justify-center">
                    <img
                      src={tabRightSideContent.src}
                      alt={tabRightSideContent.alt}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              ) : (
                <a
                  href="https://app.usemotion.com/meet/mark-evgenev/desync"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block"
                >
                  <div className="relative glass rounded-2xl p-10 text-center hover:scale-105 transition-all duration-300 neon-border">
                    <div className="w-20 h-20 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-4xl">💬</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Custom Solutions</h3>
                    <p className="text-gray-300 mb-4">Let's discuss your specific data needs</p>
                    <span className="inline-flex items-center gap-2 text-[#25BC82] font-semibold group-hover:gap-3 transition-all duration-300">
                      Schedule Call <span>→</span>
                    </span>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* NVIDIA Inception Badge Section */}
      <section className="w-full px-6 pb-24">
        <div className="max-w-7xl mx-auto text-center">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-8" />
          <div className="flex items-center justify-center">
            <img
              src="/images/inception_badges/for-screen/nvidia-inception-program-badge-rgb-for-screen.svg"
              alt="NVIDIA Inception program badge"
              className="h-16 md:h-20 lg:h-24 w-auto opacity-90"
            />
          </div>
        </div>
      </section>
    </section>
  );
}

export default Home;
